import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FaSeedling, 
  FaIndustry, 
  FaGlobeAmericas,
  FaSolarPanel,
  FaTrophy,
  FaHandshake,
  FaCertificate,
  FaCalendarAlt,
  FaArrowRight,
  FaBalanceScale,
  FaLightbulb,
  FaRecycle
} from 'react-icons/fa';
import { GiSunrise } from 'react-icons/gi';
import { MdTimeline } from 'react-icons/md';

/**
 * ═══════════════════════════════════════════════════════════════
 * 📖 OUR STORY PAGE - COMPANY JOURNEY & TIMELINE
 * ═══════════════════════════════════════════════════════════════
 * 
 * 🌍 MULTI-LANGUAGE SUPPORT:
 * Translation keys: story.*
 * 
 * 📋 SECTIONS:
 * 1. Hero with company introduction
 * 2. Timeline milestones (2013-2024)
 * 3. Core values showcase
 * 4. Call-to-action
 * 
 * ═══════════════════════════════════════════════════════════════
 */

const Story = () => {
  const { t } = useTranslation();

  const timeline = [
    {
      year: t('story.timeline.2013.year'),
      title: t('story.timeline.2013.title'),
      icon: <FaSeedling />,
      color: 'from-green-500 to-emerald-600',
      description: t('story.timeline.2013.description'),
      image: '/images/story/2013-founding.webp',
      imageAlt: t('story.timeline.2013.imageAlt')
    },
    {
      year: t('story.timeline.2017.year'),
      title: t('story.timeline.2017.title'),
      icon: <FaIndustry />,
      color: 'from-blue-500 to-cyan-600',
      description: t('story.timeline.2017.description'),
      image: '/images/story/2017-facility.webp',
      imageAlt: t('story.timeline.2017.imageAlt')
    },
    {
      year: t('story.timeline.2018.year'),
      title: t('story.timeline.2018.title'),
      icon: <FaCertificate />,
      color: 'from-purple-500 to-pink-600',
      description: t('story.timeline.2018.description'),
      image: '/images/story/2018-incorporation.webp',
      imageAlt: t('story.timeline.2018.imageAlt')
    },
    {
      year: t('story.timeline.2019.year'),
      title: t('story.timeline.2019.title'),
      icon: <FaGlobeAmericas />,
      color: 'from-orange-500 to-red-600',
      description: t('story.timeline.2019.description'),
      image: '/images/story/2019-export.webp',
      imageAlt: t('story.timeline.2019.imageAlt')
    },
    {
      year: t('story.timeline.2022.year'),
      title: t('story.timeline.2022.title'),
      icon: <FaSolarPanel />,
      color: 'from-yellow-500 to-amber-600',
      description: t('story.timeline.2022.description'),
      image: '/images/story/2022-solar.webp',
      imageAlt: t('story.timeline.2022.imageAlt')
    },
    {
      year: t('story.timeline.2023.year'),
      title: t('story.timeline.2023.title'),
      icon: <FaTrophy />,
      color: 'from-indigo-500 to-purple-600',
      description: t('story.timeline.2023.description'),
      image: '/images/story/2023-awards.webp',
      imageAlt: t('story.timeline.2023.imageAlt')
    }
  ];

  const values = [
    { 
      icon: <FaBalanceScale />, 
      title: t('story.values.balance.title'), 
      color: 'text-blue-600',
      description: t('story.values.balance.description')
    },
    { 
      icon: <FaLightbulb />, 
      title: t('story.values.explore.title'), 
      color: 'text-amber-600',
      description: t('story.values.explore.description')
    },
    { 
      icon: <FaRecycle />, 
      title: t('story.values.sustainable.title'), 
      color: 'text-green-600',
      description: t('story.values.sustainable.description')
    },
    { 
      icon: <FaHandshake />, 
      title: t('story.values.trust.title'), 
      color: 'text-purple-600',
      description: t('story.values.trust.description')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Navbar Spacer */}
      <div className="h-10"></div>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <span className="px-6 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-full text-sm font-semibold shadow-lg inline-block mb-6">
              {t('story.hero.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
              <span className="text-gray-900 dark:text-white">{t('story.hero.title')} </span>
              <span className="text-orange-600 dark:text-orange-500">{t('story.hero.titleHighlight')}</span>
            </h1>
          </motion.div>

          {/* Hero Intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl mb-12"
          >
            <div className="text-center mb-8">
              <GiSunrise className="text-7xl text-orange-600 mx-auto mb-6" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                {t('story.hero.subtitle')}
              </h2>
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-4xl mx-auto">
                {t('story.hero.description')}
              </p>
            </div>

            {/* Hero Image */}
            <div className="relative h-96 rounded-2xl overflow-hidden">
              <img 
                src="/images/story/hero-sunrise.webp" 
                alt={t('story.hero.imageAlt')}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              {/* Fallback gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-400 via-amber-500 to-yellow-500 hidden items-center justify-center text-white text-center p-8">
                <div>
                  <div className="text-9xl mb-4">🌅</div>
                  <p className="text-sm italic">
                    {t('story.hero.imageAlt')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Timeline Section */}
      <section className="container mx-auto px-4 pb-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <div className="text-center mb-16">
            <MdTimeline className="text-6xl text-orange-600 mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('story.timeline.sectionTitle')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {t('story.timeline.sectionSubtitle')}
            </p>
          </div>

          {/* Timeline Items */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-green-500 via-blue-500 to-purple-500"></div>

            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className={`relative mb-16 md:mb-24 ${
                  index % 2 === 0 ? 'md:pr-12' : 'md:pl-12 md:ml-auto'
                } md:w-1/2`}
              >
                {/* Timeline Dot */}
                <div className={`hidden md:flex absolute top-8 ${
                  index % 2 === 0 ? 'right-0 translate-x-1/2' : 'left-0 -translate-x-1/2'
                } w-12 h-12 bg-gradient-to-br ${item.color} rounded-full items-center justify-center text-white text-2xl shadow-lg z-10`}>
                  {item.icon}
                </div>

                {/* Timeline Card */}
                <motion.div
                  whileHover={{ scale: 1.02, y: -5 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
                >
                  {/* Year Badge */}
                  <div className={`bg-gradient-to-r ${item.color} px-6 py-3 flex items-center justify-between`}>
                    <div className="flex items-center gap-3">
                      <div className="text-3xl text-white md:hidden">
                        {item.icon}
                      </div>
                      <span className="text-2xl font-bold text-white">{item.year}</span>
                    </div>
                    <FaCalendarAlt className="text-white text-xl" />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      {item.title}
                    </h3>

                    {/* Image */}
                    {item.image && (
                      <div className="mb-4 rounded-xl overflow-hidden">
                        <img 
                          src={item.image} 
                          alt={item.imageAlt}
                          className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            e.target.parentElement.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                      {item.description}
                    </p>

                    {/* Image Alt Text */}
                    {item.image && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                        {item.imageAlt}
                      </p>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Values Section */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('story.values.sectionTitle')}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {t('story.values.sectionSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg text-center"
              >
                <div className={`text-5xl ${value.color} mb-4 flex justify-center`}>
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto bg-gradient-to-r from-orange-600 to-amber-600 rounded-3xl p-8 md:p-12 text-center shadow-2xl"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('story.cta.title')}
          </h2>
          <p className="text-lg text-white/90 mb-8">
            {t('story.cta.subtitle')}
          </p>
          <Link
            to="../products/king-coconut-water"
            className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            {t('story.cta.button')}
            <FaArrowRight />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default Story;
